import React from 'react';

import cloneDeep from 'lodash/cloneDeep';

export const errorMap = {
  GENERIC: {
    title: 'An error occurred',
    body: {
      title: '',
      description:
        'Please verify that your printer is connected via USB and the Zebra Browser Print application is running. If the error persists, contact support.',
    },
  },
  EMPTY_API_RESPONSE: {
    title: 'Could not print',
    body: {
      title: 'There are no labels to print.',
      description: (
        <span>
          View printing labels{' '}
          <a
            href="https://retail-support.lightspeedhq.com/hc/articles/228842627-Printing-labels"
            target="_blank"
            rel="noopener noreferrer"
          >
            documentation
          </a>
        </span>
      ),
    },
  },
  NO_PRINTER: {
    title: 'Could not print - Printer not connected',
    body: {
      title: 'Connect a Zebra Printer',
      description:
        'Make sure the USB cord is connected and fully inserted into both the printer and the computer and that the printer is powered on.',
    },
  },
  OUT_OF_MEDIA: {
    title: 'Could not print - Out of Media',
    status: ['00000001', '00010001'],
    body: {
      title: 'Load the Zebra Printer with labels',
      description:
        'The Zebra Printer is out of paper. You can print labels after loading the Zebra Printer with labels.',
    },
  },
  PAUSED: {
    title: 'Could not print - Printer is Paused',
    status: '00010000',
    body: {
      title: 'Press the Pause (⏸ ) button',
      description:
        'The Zebra Printer cannot complete the print job because it is in a pause state. Press the Pause button again to resume the print job.',
    },
  },
  OPENED: {
    title: 'Could not print - Printer is Open',
    status: ['00000004', '00010004', '00000005', '00010005'],
    body: {
      title: 'Close Printer Cover',
      description:
        "The Zebra Printer's top cover is open. Make sure the top cover is completely closed and try printing again.",
    },
  },
} as const;

/**
 * Type of error status codes and their description
 * @private
 */
const errors = cloneDeep(errorMap) as any;

/**
 * Class LabelPrintErrors handles the Error display
 * of the Zebra printers lp2824 and zd410
 * @static
 */
export default class LabelPrintErrors {
  /**
   * Get the list of errors
   * @returns {Object}
   */
  static get errors() {
    return errors;
  }

  /**
   * Get an error type string by its status code value
   * @param {String} status - Zebra's Error status code received by the status command
   * @returns {*} The error constant as a string
   */
  static getErrorFromStatus(rawStatus: string) {
    if (!rawStatus) return;
    // Match any status code that contains at least 1 non-zero digit
    // Meaning that a Warning or an Error was received from the printer
    const match = rawStatus.match(/(?=0*[1-9])\d{8}/);
    if (!match || !match[0]) return;
    const status = match[0];

    // eslint-disable-next-line no-restricted-syntax
    for (const error in errors) {
      // eslint-disable-next-line no-continue
      if (!errors[error].status) continue;

      if (!Array.isArray(errors[error].status)) {
        errors[error].status = [errors[error].status];
      }
      if (~errors[error].status.indexOf(status)) {
        // eslint-disable-next-line consistent-return
        return error;
      }
    }
  }
}
